import React, { useState } from 'react';
import axios from 'axios';
import { Upload, FileUp, Link as LinkIcon, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function VideoUpload() {
  const [singleUpload, setSingleUpload] = useState({ content_url: '', affiliate_link: '' });
  const [bulkFile, setBulkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleSingleUpload = async (e) => {
    e.preventDefault();
    
    if (!singleUpload.content_url) {
      toast.error('Please provide a content URL');
      return;
    }

    try {
      setUploading(true);
      await axios.post(`${API}/videos`, singleUpload);
      toast.success('Video uploaded successfully!');
      setSingleUpload({ content_url: '', affiliate_link: '' });
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 2000);
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', bulkFile);

      const response = await axios.post(`${API}/videos/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`${response.data.videos_created} videos uploaded successfully!`);
      
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      
      setBulkFile(null);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 2000);
    } catch (error) {
      console.error('Error bulk uploading videos:', error);
      toast.error('Failed to bulk upload videos');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = 'content_url,affiliate_link\n' +
      'https://www.instagram.com/reel/example1,https://app.emergent.sh/?via=influencer1\n' +
      'https://www.tiktok.com/@user/video/example2,https://app.emergent.sh/?via=influencer2\n' +
      'https://www.youtube.com/shorts/example3,';  // Example with empty affiliate link
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_videos.csv';
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="video-upload-container">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" data-testid="upload-title">Upload Videos</h1>
        <p className="text-gray-400 mt-1">Add influencer videos for tracking</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Single Upload Form */}
        <div className="glass-card rounded-xl p-6" data-testid="single-upload-form">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Single Video Upload</h2>
              <p className="text-sm text-gray-400">Add one video at a time</p>
            </div>
          </div>

          <form onSubmit={handleSingleUpload} className="space-y-4">
            <div>
              <Label htmlFor="content_url" className="text-gray-300">Content URL</Label>
              <Input
                id="content_url"
                data-testid="single-content-url-input"
                type="url"
                placeholder="https://instagram.com/reel/..."
                value={singleUpload.content_url}
                onChange={(e) => setSingleUpload({ ...singleUpload, content_url: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Instagram, TikTok, or YouTube video URL</p>
            </div>

            <div>
              <Label htmlFor="affiliate_link" className="text-gray-300">Affiliate Link <span className="text-gray-500 text-xs">(Optional)</span></Label>
              <Input
                id="affiliate_link"
                data-testid="single-affiliate-link-input"
                type="url"
                placeholder="https://app.emergent.sh/?via=INFLUENCER_NAME"
                value={singleUpload.affiliate_link}
                onChange={(e) => setSingleUpload({ ...singleUpload, affiliate_link: e.target.value })}
                className="mt-2 bg-white/5 border-white/10 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty if not applicable</p>
            </div>

            <Button
              type="submit"
              data-testid="single-upload-submit-btn"
              disabled={uploading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {uploading ? (
                <span className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </span>
              ) : uploadSuccess ? (
                <span className="flex items-center space-x-2">
                  <Check className="w-4 h-4" />
                  <span>Uploaded!</span>
                </span>
              ) : (
                <span className="flex items-center space-x-2">
                  <Upload className="w-4 h-4" />
                  <span>Upload Video</span>
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Bulk Upload Form */}
        <div className="glass-card rounded-xl p-6" data-testid="bulk-upload-form">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <FileUp className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Bulk CSV Upload</h2>
              <p className="text-sm text-gray-400">Upload multiple videos at once</p>
            </div>
          </div>

          <form onSubmit={handleBulkUpload} className="space-y-4">
            <div>
              <Label className="text-gray-300">CSV File</Label>
              <div
                className="mt-2 upload-zone border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer"
                onClick={() => document.getElementById('bulk-file-input').click()}
              >
                <input
                  id="bulk-file-input"
                  data-testid="bulk-file-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="hidden"
                />
                {bulkFile ? (
                  <div className="flex flex-col items-center space-y-2">
                    <FileUp className="w-12 h-12 text-green-400" />
                    <p className="text-white font-medium">{bulkFile.name}</p>
                    <p className="text-sm text-gray-400">Click to change file</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center space-y-2">
                    <FileUp className="w-12 h-12 text-gray-400" />
                    <p className="text-white">Click to select CSV file</p>
                    <p className="text-sm text-gray-400">or drag and drop</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                data-testid="download-sample-csv-btn"
                onClick={downloadSampleCSV}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Download Sample CSV
              </Button>
              
              <Button
                type="submit"
                data-testid="bulk-upload-submit-btn"
                disabled={uploading || !bulkFile}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {uploading ? 'Uploading...' : uploadSuccess ? 'Uploaded!' : 'Upload CSV'}
              </Button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-300 font-medium mb-2">CSV Format:</p>
            <code className="text-xs text-gray-300 block">
              content_url, affiliate_link (optional)
            </code>
            <p className="text-xs text-gray-400 mt-2">Platform and timestamp are auto-detected! Affiliate link can be left empty.</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">📋 How It Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
              <span className="text-blue-400 font-bold">1</span>
            </div>
            <h4 className="text-white font-medium mb-2">Upload Videos</h4>
            <p className="text-sm text-gray-400">
              Add video URLs and optional affiliate links. Platform is auto-detected from URL.
            </p>
          </div>
          
          <div>
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
              <span className="text-purple-400 font-bold">2</span>
            </div>
            <h4 className="text-white font-medium mb-2">Automatic Tracking</h4>
            <p className="text-sm text-gray-400">
              System fetches metrics every 15 mins using Apify API. Tracks views, likes, comments.
            </p>
          </div>
          
          <div>
            <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center mb-3">
              <span className="text-green-400 font-bold">3</span>
            </div>
            <h4 className="text-white font-medium mb-2">View Analytics</h4>
            <p className="text-sm text-gray-400">
              Check dashboard for performance metrics and influencer rankings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoUpload;